/**
 * StakeholdersPanel — directory of Internal + External stakeholders for
 * the client. Admin/Manager can add / edit / soft-delete. Used by
 * StaffManagement under a new "Stakeholders" tab.
 *
 * No login, no invite — these are contact records the incident XLSX
 * export pulls from when filling sheet 7 ("Stakeholder List").
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Stakeholder, StakeholderCategory, StakeholderDTO,
  listStakeholders, createStakeholder, updateStakeholder, deleteStakeholder,
} from '../api/stakeholders';
import {
  INTERNAL_STAKEHOLDER_ROLES, EXTERNAL_STAKEHOLDER_ROLES,
} from '../constants/stakeholderRoles';

interface Props {
  /** Admin/Manager can edit; others are read-only. */
  canManage: boolean;
  /** Controlled "show add modal" flag so the parent (StaffManagement
   *  header) can trigger it via an external button. */
  showAddModal: boolean;
  onCloseAddModal: () => void;
}

export function StakeholdersPanel({ canManage, showAddModal, onCloseAddModal }: Props) {
  const [list,  setList]  = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [edit, setEdit]   = useState<Stakeholder | null>(null);
  const [filterCat, setFilterCat] = useState<'' | StakeholderCategory>('');
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true);
    try { setList(await listStakeholders()); setError(''); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((s) => {
      if (filterCat && s.category !== filterCat) return false;
      if (q) return [s.name, s.role, s.email, s.contact]
        .some((v) => v && v.toLowerCase().includes(q));
      return true;
    });
  }, [list, filterCat, search]);

  const internalCount = list.filter(s => s.category === 'INTERNAL').length;
  const externalCount = list.filter(s => s.category === 'EXTERNAL').length;

  return (
    <div style={{ padding: '14px 18px 18px' }}>
      {/* Summary + filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          <strong style={{ color: 'var(--fg)' }}>{list.length}</strong> stakeholders
          {' · '}<span style={{ color: 'var(--primary)' }}>{internalCount} internal</span>
          {' · '}<span style={{ color: 'var(--muted)' }}>{externalCount} external</span>
        </div>
        <div style={{ flex: 1 }} />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / role / email…"
          style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--fg)',
            fontFamily: 'inherit', width: 240,
          }}
        />
        <select
          value={filterCat} onChange={(e) => setFilterCat(e.target.value as any)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--fg)',
            fontFamily: 'inherit',
          }}
        >
          <option value="">All categories</option>
          <option value="INTERNAL">Internal only</option>
          <option value="EXTERNAL">External only</option>
        </select>
      </div>

      {error && <p style={{ color: 'var(--accent-red)', fontSize: 12 }}>⚠ {error}</p>}
      {loading ? (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <div style={{
          border: '2px dashed var(--border-md)', borderRadius: 10,
          padding: '28px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13,
        }}>
          {list.length === 0
            ? 'No stakeholders yet. Click "+ Add Stakeholder" to add the first one.'
            : 'No stakeholders match the current filter.'}
        </div>
      ) : (
        <div style={{ fontSize: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 1.3fr 160px' }}>
            <HeaderCell>Name</HeaderCell>
            <HeaderCell>Role</HeaderCell>
            <HeaderCell>Contact</HeaderCell>
            <HeaderCell>Email</HeaderCell>
            <HeaderCell />
          </div>
          {filtered.map((s) => (
            <div key={s.id} className="sh-row" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 1.3fr 160px' }}>
              <Cell>
                <div style={{ fontWeight: 600, color: 'var(--fg)' }}>{s.name}</div>
                <div style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: 0.5, marginTop: 2,
                  display: 'inline-block', padding: '1px 7px', borderRadius: 4,
                  background: s.category === 'INTERNAL' ? 'var(--primary-dim)' : 'var(--surface)',
                  border: `1px solid ${s.category === 'INTERNAL' ? 'var(--primary-border)' : 'var(--border-md)'}`,
                  color: s.category === 'INTERNAL' ? 'var(--primary)' : 'var(--muted)',
                }}>
                  {s.category}
                </div>
              </Cell>
              <Cell>{s.role}</Cell>
              <Cell>{s.contact || <em style={{ color: 'var(--subtle)' }}>—</em>}</Cell>
              <Cell>{s.email || <em style={{ color: 'var(--subtle)' }}>—</em>}</Cell>
              <Cell>
                {canManage && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEdit(s)} style={btnStyle}>Edit</button>
                    <button onClick={async () => {
                      if (!window.confirm(`Remove ${s.name}?`)) return;
                      try { await deleteStakeholder(s.id); load(); }
                      catch (e: any) { alert(e.message); }
                    }} style={{ ...btnStyle, color: 'var(--accent-red)' }}>Remove</button>
                  </div>
                )}
              </Cell>
            </div>
          ))}
        </div>
      )}

      {(showAddModal || edit) && (
        <StakeholderModal
          existing={edit}
          onClose={() => { onCloseAddModal(); setEdit(null); }}
          onSaved={() => { onCloseAddModal(); setEdit(null); load(); }}
        />
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--border-md)',
  borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600,
  color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

function HeaderCell({ children }: { children?: React.ReactNode }) {
  return (
    <div style={{
      padding: '8px 10px', background: 'var(--surface)',
      borderBottom: '1px solid var(--border-md)',
      fontSize: 10, fontWeight: 700, color: 'var(--subtle)',
      letterSpacing: 0.5, textTransform: 'uppercase',
    }}>{children}</div>
  );
}
function Cell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '10px', borderBottom: '1px solid var(--border)',
      fontSize: 12, color: 'var(--fg)',
      // overflowWrap: only breaks unbreakable strings (long emails).
      // wordBreak: 'break-word' was hyphenating short words like
      // "Edit"/"Remove" when columns ran tight, producing "Edi / t".
      overflowWrap: 'anywhere', wordBreak: 'normal',
      minWidth: 0,
    }}>{children}</div>
  );
}

// ── Add / Edit modal ────────────────────────────────────────────────────
function StakeholderModal({
  existing, onClose, onSaved,
}: {
  existing: Stakeholder | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [category, setCategory] = useState<StakeholderCategory>(existing?.category || 'INTERNAL');
  const [role, setRole]         = useState(existing?.role || '');
  const [name, setName]         = useState(existing?.name || '');
  const [contact, setContact]   = useState(existing?.contact || '');
  const [email, setEmail]       = useState(existing?.email || '');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const roleOptions = category === 'INTERNAL' ? INTERNAL_STAKEHOLDER_ROLES : EXTERNAL_STAKEHOLDER_ROLES;

  // When switching category, clear role if the existing one isn't in the new list.
  // roleOptions is derived from `category`, so listing `category` alone is enough —
  // we intentionally don't depend on `role` here (would cause a clear-loop).
  useEffect(() => {
    setRole((r) => (r && roleOptions.includes(r) ? r : ''));
  }, [category, roleOptions]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (!role) { setErr('Please pick a role.'); return; }
    if (!name.trim()) { setErr('Name is required.'); return; }
    setSaving(true);
    try {
      const dto: StakeholderDTO = { category, role, name: name.trim(), contact: contact.trim() || undefined, email: email.trim() || undefined };
      if (existing) await updateStakeholder(existing.id, dto);
      else          await createStakeholder(dto);
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1005,
        background: 'rgba(12,12,18,0.62)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: 'var(--card)', border: '1px solid var(--border-md)',
          borderRadius: 14, padding: '22px 24px', maxWidth: 480, width: '92%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--fg)' }}>
            {existing ? 'Edit stakeholder' : 'Add stakeholder'}
          </h3>
          <button
            type="button" onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer', padding: 0 }}
          >×</button>
        </div>

        {/* Category tabs — match Crisis Shield primary purple (theme-aware) */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--surface)', padding: 3, borderRadius: 8, border: '1px solid var(--border)' }}>
          {(['INTERNAL', 'EXTERNAL'] as StakeholderCategory[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              style={{
                flex: 1, padding: '7px 12px', fontSize: 12, fontWeight: 700,
                border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                background: category === c ? 'var(--primary-dim)' : 'transparent',
                color:      category === c ? 'var(--primary)'    : 'var(--muted)',
                transition: 'background 120ms, color 120ms',
              }}
            >
              {c === 'INTERNAL' ? 'Internal' : 'External'}
            </button>
          ))}
        </div>

        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Kim" style={inputStyle} maxLength={255} />
        </Field>

        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle}>
            <option value="">— Select role —</option>
            {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>

        <Field label="Contact (phone)">
          <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="0412 345 678" style={inputStyle} maxLength={120} />
        </Field>

        <Field label="Email">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" type="email" style={inputStyle} maxLength={255} />
        </Field>

        {err && <div style={{ color: 'var(--accent-red)', fontSize: 12 }}>⚠ {err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            type="button" onClick={onClose} disabled={saving}
            style={{
              background: 'transparent', border: '1px solid var(--border-md)',
              color: 'var(--muted)', borderRadius: 6, padding: '6px 14px',
              fontSize: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >Cancel</button>
          <button
            type="submit" disabled={saving}
            className="submit-btn"
            style={{
              padding: '7px 16px',
              fontSize: 12, fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
              fontFamily: 'inherit',
              opacity: saving ? 0.6 : 1,
            }}
          >{saving ? 'Saving…' : existing ? 'Save changes' : 'Add stakeholder'}</button>
        </div>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 6, padding: '7px 10px', fontSize: 13, color: 'var(--fg)',
  fontFamily: 'inherit', outline: 'none',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: 'var(--subtle)', textTransform: 'uppercase' }}>
        {label}
      </label>
      {children}
    </div>
  );
}
